import { useReducer, useMemo, useCallback, useState } from 'react'
import { COLUMN_CATALOG_MAP, DEFAULT_CONFIG, GROUPING_PRESETS, createColumnInstance, regenerateColumnIds, getStatisticDisplay } from '../lib/constants'
import { migrateConfig, CURRENT_CONFIG_VERSION } from '../lib/migrateConfig'
import { resolveComparisonColumnRefs } from '../lib/comparisonColumns'
import { resolveWindowColumnRefs } from '../lib/windowColumns'
import { resolveColumnHeader } from '../lib/resolveColumnHeader'
import { makeChartInstanceId } from '../lib/chartInstanceId'

const actions = {
  SET_GROUPINGS: 'SET_GROUPINGS',
  ADD_GROUPING: 'ADD_GROUPING',
  REMOVE_GROUPING: 'REMOVE_GROUPING',
  REORDER_GROUPINGS: 'REORDER_GROUPINGS',
  ADD_COLUMN: 'ADD_COLUMN',
  REMOVE_COLUMN: 'REMOVE_COLUMN',
  SET_COLUMNS: 'SET_COLUMNS',
  SET_QUICK_DATE: 'SET_QUICK_DATE',
  SET_DATE_RANGE: 'SET_DATE_RANGE',
  SET_FETCH_ALL_LEVELS: 'SET_FETCH_ALL_LEVELS',
  SET_INCLUDE_BENCHMARKS: 'SET_INCLUDE_BENCHMARKS',
  SET_GROUPING_PRESET: 'SET_GROUPING_PRESET',
  SET_SCOPE_FILTER: 'SET_SCOPE_FILTER',
  CLEAR_SCOPE_FILTER: 'CLEAR_SCOPE_FILTER',
  SET_SCOPE_FILTERS: 'SET_SCOPE_FILTERS',
  ADD_DEFAULT_CHART: 'ADD_DEFAULT_CHART',
  REMOVE_DEFAULT_CHART: 'REMOVE_DEFAULT_CHART',
  UPDATE_DEFAULT_CHART: 'UPDATE_DEFAULT_CHART',
  SET_DEFAULT_CHARTS: 'SET_DEFAULT_CHARTS',
  LOAD_CONFIG: 'LOAD_CONFIG',
  RESET: 'RESET'
}

/**
 * When the set of selected groupings changes, drop any scopeFilters keyed
 * to groupings that are no longer present. Keeps the saved view shape
 * tidy and avoids stale filters silently re-attaching if the user later
 * re-adds the grouping.
 */
function pruneScopeFilters (scopeFilters, groupings) {
  if (!scopeFilters) return scopeFilters
  const allowed = new Set(groupings)
  const next = {}
  let changed = false
  for (const [key, value] of Object.entries(scopeFilters)) {
    if (allowed.has(key)) next[key] = value
    else changed = true
  }
  return changed ? next : scopeFilters
}

function reducer (state, action) {
  switch (action.type) {
    case actions.SET_GROUPINGS:
      return {
        ...state,
        groupings: action.payload,
        groupingPreset: 'custom',
        scopeFilters: pruneScopeFilters(state.scopeFilters, action.payload)
      }
    case actions.ADD_GROUPING:
      if (state.groupings.includes(action.payload)) return state
      return { ...state, groupings: [...state.groupings, action.payload], groupingPreset: 'custom' }
    case actions.REMOVE_GROUPING: {
      const groupings = state.groupings.filter(g => g !== action.payload)
      return {
        ...state,
        groupings,
        groupingPreset: 'custom',
        scopeFilters: pruneScopeFilters(state.scopeFilters, groupings)
      }
    }
    case actions.REORDER_GROUPINGS:
      return { ...state, groupings: action.payload, groupingPreset: 'custom' }
    case actions.SET_GROUPING_PRESET: {
      const preset = GROUPING_PRESETS.find(p => p.value === action.payload)
      if (!preset || preset.value === 'custom') {
        return { ...state, groupingPreset: 'custom' }
      }
      return {
        ...state,
        groupingPreset: preset.value,
        groupings: preset.groupings,
        scopeFilters: pruneScopeFilters(state.scopeFilters, preset.groupings)
      }
    }
    case actions.SET_SCOPE_FILTER: {
      // payload: { grouping, filter }. A null filter clears the entry.
      const next = { ...(state.scopeFilters || {}) }
      if (action.payload.filter == null) {
        delete next[action.payload.grouping]
      } else {
        next[action.payload.grouping] = action.payload.filter
      }
      return { ...state, scopeFilters: next }
    }
    case actions.CLEAR_SCOPE_FILTER: {
      if (!state.scopeFilters || !(action.payload in state.scopeFilters)) return state
      const next = { ...state.scopeFilters }
      delete next[action.payload]
      return { ...state, scopeFilters: next }
    }
    case actions.SET_SCOPE_FILTERS: {
      // Bulk-replace the whole scopeFilters map (dialog Apply path).
      // Pruned against the current grouping list so stale entries can't
      // sneak back in via a saved view.
      const next = pruneScopeFilters(action.payload || {}, state.groupings)
      return { ...state, scopeFilters: next }
    }
    case actions.ADD_COLUMN: {
      const instance = action.payload
      if (!instance) return state
      return { ...state, columns: [...state.columns, instance] }
    }
    case actions.REMOVE_COLUMN:
      return { ...state, columns: state.columns.filter(c => c.id !== action.payload) }
    case actions.SET_COLUMNS:
      return { ...state, columns: action.payload }
    case actions.ADD_DEFAULT_CHART: {
      // Append a new ChartInstance. Duplicates of the same catalog id
      // are intentional -- e.g., two Allocation tiles each configured
      // for a different measure. Caller passes a fully-formed instance
      // (id + catalogId/toolId) so the reducer doesn't need to know
      // about the catalog.
      const current = state.defaultCharts || []
      const instance = action.payload
      if (!instance || !instance.id) return state
      return { ...state, defaultCharts: [...current, instance] }
    }
    case actions.REMOVE_DEFAULT_CHART: {
      // Payload is the instance.id (stable across renders since v8).
      const current = state.defaultCharts || []
      const next = current.filter(inst => inst.id !== action.payload)
      if (next.length === current.length) return state
      return { ...state, defaultCharts: next }
    }
    case actions.UPDATE_DEFAULT_CHART: {
      // Patches the named instance in place. `action.payload` is
      // `{ id, patch }` where patch is shallow-merged onto the
      // instance. `patch.payload` is shallow-merged onto the instance's
      // existing payload so callers can update individual fields
      // (e.g. `{ payload: { chartType: 'bar' } }`) without rebuilding
      // the whole payload object. Anything else on the patch
      // (catalogId, toolId) replaces wholesale -- we don't have a
      // use case for partial updates of those today.
      const current = state.defaultCharts || []
      const { id, patch } = action.payload || {}
      if (!id || !patch) return state
      let changed = false
      const next = current.map(inst => {
        if (inst.id !== id) return inst
        changed = true
        const { payload: payloadPatch, ...restPatch } = patch
        const mergedPayload = payloadPatch
          ? { ...(inst.payload || {}), ...payloadPatch }
          : inst.payload
        return { ...inst, ...restPatch, payload: mergedPayload }
      })
      if (!changed) return state
      return { ...state, defaultCharts: next }
    }
    case actions.SET_DEFAULT_CHARTS:
      // Bulk-replace (used by LOAD_CONFIG via the migrated config).
      return { ...state, defaultCharts: Array.isArray(action.payload) ? action.payload : [] }
    case actions.SET_QUICK_DATE:
      return { ...state, dateMode: 'quickDate', quickDate: action.payload, startDate: null, endDate: null }
    case actions.SET_DATE_RANGE:
      return { ...state, dateMode: 'dateRange', quickDate: null, startDate: action.payload.startDate, endDate: action.payload.endDate }
    case actions.SET_FETCH_ALL_LEVELS:
      return { ...state, fetchAllLevels: action.payload }
    case actions.SET_INCLUDE_BENCHMARKS:
      return { ...state, includeBenchmarks: action.payload }
    case actions.LOAD_CONFIG: {
      const migrated = migrateConfig(action.payload.configuration, action.payload.configVersion)
      const regenerated = regenerateColumnIds(migrated.columns || [])
      const columns = resolveComparisonColumnRefs(
        resolveWindowColumnRefs(regenerated, COLUMN_CATALOG_MAP),
        COLUMN_CATALOG_MAP
      )
      return { ...migrated, columns }
    }
    case actions.RESET:
      return { ...DEFAULT_CONFIG }
    default:
      return state
  }
}

export function useExplorerConfig () {
  const [config, dispatch] = useReducer(reducer, DEFAULT_CONFIG)

  const columnDefinitions = useMemo(() => {
    return config.columns.map(col => {
      const catalog = COLUMN_CATALOG_MAP[col.columnType]
      if (!catalog) return null

      const header = resolveColumnHeader(col, catalog, config.columns)

      let format = catalog.format
      let measureKind = catalog.measureKind
      if (catalog.kind === 'comparison') {
        const op = col.options?.operation || 'difference'
        if (op === 'ratio' || op === 'percentChange') {
          format = 'returns'
          measureKind = 'ratio'
        } else {
          const instA = col.options?.columnAId ? config.columns.find(c => c.id === col.options.columnAId) : null
          const catA = instA ? COLUMN_CATALOG_MAP[instA.columnType] : null
          format = catA?.format || 'marketValue'
          measureKind = catA?.measureKind || 'currency'
        }
      } else if (col.columnType === 'groupStatistic' || col.columnType === 'benchmarkStatistic') {
        // Statistic columns: format/measureKind depend on the selected
        // `statisticType` because OAT returns different units per
        // statistic (decimal-percent / dimensionless / pre-scaled / etc.).
        // See STATISTIC_DISPLAY in lib/constants.js for the mapping; the
        // catalog row's hardcoded format/measureKind acts as the
        // Std-Deviation default that getStatisticDisplay falls back to.
        const display = getStatisticDisplay(col.options?.statisticType)
        format = display.format
        measureKind = display.measureKind
      }

      return {
        accessorKey: col.id,
        header,
        format,
        measureKind,
        align: catalog.align
      }
    }).filter(Boolean)
  }, [config.columns])

  const [cleanSnapshot, setCleanSnapshot] = useState(null)

  const loadConfig = useCallback((savedConfig) => {
    dispatch({ type: actions.LOAD_CONFIG, payload: savedConfig })
    setCleanSnapshot(JSON.stringify({
      configuration: savedConfig.configuration,
      configVersion: savedConfig.configVersion ?? CURRENT_CONFIG_VERSION
    }))
  }, [])

  const serializeConfig = useCallback(() => ({
    configuration: {
      ...config,
      defaultCharts: Array.isArray(config.defaultCharts) ? config.defaultCharts : [],
      columns: config.columns.map(({ columnType, label, options }) => {
        const catalog = COLUMN_CATALOG_MAP[columnType]
        if (catalog?.kind === 'window' && options?.sourceColumnId) {
          const sourceIndex = config.columns.findIndex(c => c.id === options.sourceColumnId)
          const { sourceColumnId, ...rest } = options
          return {
            columnType,
            label,
            options: { ...rest, sourceColumnIndex: sourceIndex >= 0 ? sourceIndex : undefined }
          }
        }
        if (catalog?.kind === 'comparison') {
          const { columnAId, columnBId, ...rest } = options || {}
          const columnAIndex = columnAId ? config.columns.findIndex(c => c.id === columnAId) : -1
          const columnBIndex = columnBId ? config.columns.findIndex(c => c.id === columnBId) : -1
          return {
            columnType,
            label,
            options: {
              ...rest,
              columnAIndex: columnAIndex >= 0 ? columnAIndex : undefined,
              columnBIndex: columnBIndex >= 0 ? columnBIndex : undefined
            }
          }
        }
        return { columnType, label, options }
      })
    },
    configVersion: CURRENT_CONFIG_VERSION
  }), [config])

  const isDirty = useMemo(() => {
    if (!cleanSnapshot) return false
    return JSON.stringify(serializeConfig()) !== cleanSnapshot
  }, [serializeConfig, cleanSnapshot])

  const markClean = useCallback(() => {
    setCleanSnapshot(JSON.stringify(serializeConfig()))
  }, [serializeConfig])

  return {
    config,
    columnDefinitions,
    setGroupings: (groupings) => dispatch({ type: actions.SET_GROUPINGS, payload: groupings }),
    addGrouping: (grouping) => dispatch({ type: actions.ADD_GROUPING, payload: grouping }),
    removeGrouping: (grouping) => dispatch({ type: actions.REMOVE_GROUPING, payload: grouping }),
    reorderGroupings: (groupings) => dispatch({ type: actions.REORDER_GROUPINGS, payload: groupings }),
    setGroupingPreset: (preset) => dispatch({ type: actions.SET_GROUPING_PRESET, payload: preset }),
    addColumn: (columnType, optionOverrides) => dispatch({ type: actions.ADD_COLUMN, payload: createColumnInstance(columnType, optionOverrides) }),
    removeColumn: (id) => dispatch({ type: actions.REMOVE_COLUMN, payload: id }),
    setColumns: (columns) => dispatch({ type: actions.SET_COLUMNS, payload: columns }),
    setQuickDate: (quickDate) => dispatch({ type: actions.SET_QUICK_DATE, payload: quickDate }),
    setDateRange: (startDate, endDate) => dispatch({ type: actions.SET_DATE_RANGE, payload: { startDate, endDate } }),
    setFetchAllLevels: (value) => dispatch({ type: actions.SET_FETCH_ALL_LEVELS, payload: value }),
    setIncludeBenchmarks: (value) => dispatch({ type: actions.SET_INCLUDE_BENCHMARKS, payload: value }),
    setScopeFilter: (grouping, filter) => dispatch({ type: actions.SET_SCOPE_FILTER, payload: { grouping, filter } }),
    clearScopeFilter: (grouping) => dispatch({ type: actions.CLEAR_SCOPE_FILTER, payload: grouping }),
    setScopeFilters: (filters) => dispatch({ type: actions.SET_SCOPE_FILTERS, payload: filters }),
    // addDefaultChart(spec): spec is `{ catalogId? , toolId?, payload? }`
    // -- at least one of `catalogId` / `toolId` is required. `catalogId`
    // is the preset the instance was created from (used at render to
    // resolve payload + toolId). `toolId` is for ad-hoc / non-preset
    // instances. `payload` is an optional initial payload (e.g. blank
    // chart-type entries seed minimal `{ scope, depth }` so the tool
    // can build a request on first mount); shallow-merges over the
    // catalog defaults at render time, just like user edits do. A
    // stable instance id is generated here so the caller doesn't have
    // to.
    addDefaultChart: (spec) => {
      if (!spec || (!spec.catalogId && !spec.toolId)) return
      const instance = { id: makeChartInstanceId(), ...spec }
      dispatch({ type: actions.ADD_DEFAULT_CHART, payload: instance })
    },
    // removeDefaultChart(instanceId): instance.id, NOT catalogId. Two
    // instances of the same preset have different ids and remove
    // independently.
    removeDefaultChart: (instanceId) => dispatch({ type: actions.REMOVE_DEFAULT_CHART, payload: instanceId }),
    // updateDefaultChart(instanceId, patch): patches the instance.
    // `patch.payload` (if present) is shallow-merged onto the
    // existing payload -- callers can update individual fields
    // (chartType, measureColumnInstance, etc.) without rebuilding
    // the whole payload. Tools call this through
    // `onPayloadChange` (see ChartStack + ChartTile).
    updateDefaultChart: (instanceId, patch) => {
      if (!instanceId || !patch) return
      dispatch({ type: actions.UPDATE_DEFAULT_CHART, payload: { id: instanceId, patch } })
    },
    setDefaultCharts: (instances) => dispatch({ type: actions.SET_DEFAULT_CHARTS, payload: instances }),
    loadConfig,
    serializeConfig,
    isDirty,
    markClean,
    reset: () => dispatch({ type: actions.RESET })
  }
}
